import React, { useState, useRef, useEffect } from 'react';
import {
  Activity, UserPlus, FileText, ShieldCheck, TrendingUp
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const ICON_MAP = {
  'user-plus': UserPlus,
  'file-text': FileText,
  'shield-check': ShieldCheck,
  'trending-up': TrendingUp,
};

const COLOR_MAP = {
  signup: { bg: '#EFF6FF', icon: '#2563EB' },
  loi: { bg: '#FFF7ED', icon: '#EA580C' },
  verification: { bg: '#F0FDF4', icon: '#16A34A' },
  deal: { bg: '#FAF5FF', icon: '#9333EA' },
};

const ActivityFeed = ({ activities = [] }) => {
  const { userTheme } = useTheme();
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-scroll effect
  useEffect(() => {
    if (!scrollRef.current || activities.length <= 5 || isPaused) return;

    const container = scrollRef.current;
    let animationId;
    let scrollSpeed = 0.5;

    const scroll = () => {
      if (!isPaused && container) {
        container.scrollTop += scrollSpeed;
        // Reset to top when scrolled past halfway (seamless loop)
        if (container.scrollTop >= container.scrollHeight / 2) {
          container.scrollTop = 0;
        }
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [activities.length, isPaused]);

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const d = new Date(timeStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Duplicate for seamless scroll
  const displayItems = activities.length > 5 ? [...activities, ...activities] : activities;

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100" style={{ backgroundColor: userTheme?.dashboardWidgetHeader?.backgroundColor }}>
        <Activity className="w-4 h-4 text-indigo-500" />
        <h3 className="text-sm font-semibold text-gray-800">Platform Activity</h3>
        <div className="ml-auto flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs text-gray-400">Live</span>
        </div>
      </div>

      {/* Feed */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-hidden max-h-[320px]"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {displayItems.length === 0 ? (
          <div className="p-5 text-center">
            <p className="text-sm text-gray-500">No recent activity.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {displayItems.map((item, idx) => {
              const IconComp = ICON_MAP[item.icon] || Activity;
              const colors = COLOR_MAP[item.type] || COLOR_MAP.signup;

              return (
                <div key={`${item.type}-${idx}`} className="flex items-center gap-3 px-5 py-2.5">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: colors.bg }}
                  >
                    <IconComp className="w-4 h-4" style={{ color: colors.icon }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate">{item.text}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {formatTime(item.time)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
